import { inject, Inject, Injectable, OnDestroy, PendingTasks, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, concatMap, forkJoin, from, map, take } from 'rxjs';
import {
  CraftResource,
  CraftingRequirements,
  Enchantment,
  FarmConsumption,
  FarmGrownItem,
  FarmProduct,
  FarmProducts,
  Harvest,
  Item,
  JournalCraftItemFame,
  JournalLoot,
  LootItem,
  LootList,
  ShopCategory,
  ShopSubCategory,
} from 'src/app/shared/models/items.model';
import { LocalizationData, LocalizationLanguage } from 'src/app/shared/models/localization.model';
import { Resource, ResourceTier } from 'src/app/shared/models/resources.model';
import { Achievement, AchievementBonus } from 'src/app/shared/models/achievement.model';
import { getAllLocalizationLanguages } from 'src/app/shared/constants/localization.constant';
import { UserSettingsService } from '../user-settings/user-settings.service';
import { getSlotTypeByName } from 'src/app/shared/constants/slot-types.constant';
import { FarmingModifier, FarmingYieldModifier, FarmPlot, toBiome, toClusterQuality, toContinent } from 'src/app/shared/models/farming-models/farm-models';
import { getLocation } from 'src/app/shared/constants/locations.constant';
import { isPlatformServer } from '@angular/common';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class JsonReaderService implements OnDestroy {
  // Class-scoped variables
  private _itemsData = new BehaviorSubject<Item[]>([]);
  itemsData$ = this._itemsData.asObservable();
  private itemsIndex: { [uniqueName: string]: Item } = {};

  private _shopCategoriesData = new BehaviorSubject<ShopCategory[]>([]);
  shopCategoriesData$ = this._shopCategoriesData.asObservable();

  private _achievementsData = new BehaviorSubject<Achievement[]>([]);
  achievementsData$ = this._achievementsData.asObservable();

  private _farmingModifiersData = new BehaviorSubject<FarmingModifier[]>([]);
  farmingModifiersData$ = this._farmingModifiersData.asObservable();

  private _localizationData = new BehaviorSubject<LocalizationData[]>([]);
  localizationData$ = this._localizationData.asObservable();
  private localizationIndex: { [id: string]: Map<LocalizationLanguage, string> } = {};

  private _resourcesData = new BehaviorSubject<Resource[]>([]);
  resourcesData$ = this._resourcesData.asObservable();

  private _upgradeResources = new BehaviorSubject<Item[]>([]);
  upgradeResources$ = this._upgradeResources.asObservable();

  private _hasLoaded = new BehaviorSubject<boolean>(false);
  hasLoaded$ = this._hasLoaded.asObservable();
  private _loadWasRequested: boolean = false;

  private _usNameMappingsHasLoaded = new BehaviorSubject<boolean>(false);
  usNameMappingsHasLoaded$ = this._usNameMappingsHasLoaded.asObservable();

  private shouldLog: boolean;

  public usNameMappings!: { [key: string]: string };

  //https://next.angular.dev/api/core/PendingTasks
  private pendingTasks = inject(PendingTasks);
  private hasLoadedTask!: () => void;

  constructor(
    private http: HttpClient,
    private userSettingsService: UserSettingsService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.loadUsNameMappings();

    this.shouldLog = environment.logJsonReaderTimers;
  }

  ngOnDestroy(): void {
    this._itemsData.complete();
    this._shopCategoriesData.complete();
    this._achievementsData.complete();
    this._farmingModifiersData.complete();
    this._localizationData.complete();
    this._resourcesData.complete();
    this._upgradeResources.complete();
    this._hasLoaded.complete();
    this._usNameMappingsHasLoaded.complete();

    this._itemsData = new BehaviorSubject<Item[]>([]);
    this._shopCategoriesData = new BehaviorSubject<ShopCategory[]>([]);
    this._achievementsData = new BehaviorSubject<Achievement[]>([]);
    this._farmingModifiersData = new BehaviorSubject<FarmingModifier[]>([]);
    this._localizationData = new BehaviorSubject<LocalizationData[]>([]);
    this._resourcesData = new BehaviorSubject<Resource[]>([]);
    this._upgradeResources = new BehaviorSubject<Item[]>([]);
    this._hasLoaded = new BehaviorSubject<boolean>(false);
    this._usNameMappingsHasLoaded = new BehaviorSubject<boolean>(false);

    this.localizationIndex = {};
    this.itemsIndex = {};

    this.usNameMappings = {};
  }
  ensureLoaded(): void {
    if (!this._loadWasRequested && !this._hasLoaded.value) {
      this._loadWasRequested = true;
      this.loadData().pipe(take(1)).subscribe();
      this.hasLoadedTask = this.pendingTasks.add();
    }
  }

  public loadUsNameMappings(): void {
    let url = environment.dataApiUrls.usNameMappings.client;
    if (isPlatformServer(this.platformId)) {
      url = environment.dataApiUrls.usNameMappings.server;
    }
    this.http
      .get<{ [key: string]: string }>(url)
      .pipe(
        take(1),
        map((response) => (this.usNameMappings = response))
      )
      .subscribe(() => this._usNameMappingsHasLoaded.next(true));
  }

  private loadData(): Observable<any> {
    this._hasLoaded.next(false);

    const combined$ = forkJoin({
      items: this.loadItemsJsonData(),
      localization: this.loadLocalizationJsonData(),
      resources: this.loadResourcesJsonData(),
      achievements: this.loadAchievementsJsonData(),
      farmingModifiers: this.loadFarmingModifiersJsonData(),
    });

    return combined$.pipe(
      concatMap(async (data) => {
        if (this.shouldLog) console.time('Total Time');

        if (this.shouldLog) console.time('_achievementsData');
        this._achievementsData.next(await this.loadAchievementsData(data.achievements));
        if (this.shouldLog) console.timeEnd('_achievementsData');

        if (this.shouldLog) console.time('_itemsData');
        this._itemsData.next(await this.loadItemsData(data.items));
        if (this.shouldLog) console.timeEnd('_itemsData');

        if (this.shouldLog) console.time('indexItemsByUniqueName');
        this.indexItemsByUniqueName(this._itemsData.value);
        if (this.shouldLog) console.timeEnd('indexItemsByUniqueName');

        if (this.shouldLog) console.time('_shopCategoriesData');
        this._shopCategoriesData.next(await this.loadShopCategoriesData(data.items));
        if (this.shouldLog) console.timeEnd('_shopCategoriesData');

        if (this.shouldLog) console.time('_localizationData');
        this._localizationData.next(await this.loadLocalizationData(data.localization));
        if (this.shouldLog) console.timeEnd('_localizationData');

        if (this.shouldLog) console.time('indexLocalizedNameById');
        this.indexLocalizedNameById(this._localizationData.value);
        if (this.shouldLog) console.timeEnd('indexLocalizedNameById');

        if (this.shouldLog) console.time('_resourcesData');
        this._resourcesData.next(await this.loadResourcesData(data.resources));
        if (this.shouldLog) console.timeEnd('_resourcesData');

        if (this.shouldLog) console.time('_farmingModifiersData');
        this._farmingModifiersData.next(await this.loadFarmingModifiersData(data.farmingModifiers));
        if (this.shouldLog) console.timeEnd('_farmingModifiersData');

        // Further processing using the updated BehaviorSubject values
        if (this.shouldLog) console.time('addIngredientsItems');
        this.addIngredientsAndUpgradeResItems(this._itemsData.value);
        if (this.shouldLog) console.timeEnd('addIngredientsItems');

        if (this.shouldLog) console.time('addItemsToJournals');
        this.addItemsToJournals(this._itemsData.value);
        if (this.shouldLog) console.timeEnd('addItemsToJournals');

        if (this.shouldLog) console.time('addItemsToFarmStuff');
        this.addItemsToFarmStuff(this._itemsData.value);
        if (this.shouldLog) console.timeEnd('addItemsToFarmStuff');

        if (this.shouldLog) console.time('getListOfUpgradeResources');
        this.getListOfUpgradeResources(this._itemsData.value);
        if (this.shouldLog) console.timeEnd('getListOfUpgradeResources');

        if (this.shouldLog) console.timeEnd('Total Time');

        this._hasLoaded.next(true);
        this.hasLoadedTask();
      })
    );
  }

  //#region CATEGORY HLEPER METHODS

  getItemShopCategory(item: Item): { category: ShopCategory | undefined; subCategory: ShopSubCategory | undefined } {
    let category = this._shopCategoriesData.value.find((x) => x.name === item.shopCategory);
    let subCategory = category?.subCategories.find((x) => x.name === item.shopSubCategory);
    return { category: category, subCategory: subCategory };
  }

  //#endregion

  //#region ITEM HELPER METHODS
  getJournalByItem(item: Item): { full: Item | undefined; empty: Item | undefined } {
    return {
      full: this._itemsData.value.find((x) => x.journalCraftItemFame && x.journalCraftItemFame.validItems.includes(item) && x.uniqueName.endsWith('_FULL')),
      empty: this._itemsData.value.find((x) => x.journalCraftItemFame && x.journalCraftItemFame.validItems.includes(item) && x.uniqueName.endsWith('_EMPTY')),
    };
  }
  private indexItemsByUniqueName(items: Item[]): void {
    for (let item of items) {
      this.itemsIndex[item.uniqueName.toLowerCase()] = item;
    }
  }
  getItemFromIndex(uniqueName: string): Item | undefined {
    return this.itemsIndex[uniqueName.toLowerCase()];
  }
  //#endregion

  //#region LOCALIZATION HELPER METHODS
  getItemLocalizedName(uniqueName: string, language: LocalizationLanguage): string | undefined {
    return this.localizationIndex['items_' + uniqueName.toLowerCase()]?.get(language) ?? undefined;
  }
  getShopCategoryLocalizedName(name: string, language: LocalizationLanguage): string | undefined {
    return this.localizationIndex['shopcategory_' + name.toLowerCase()]?.get(language) ?? undefined;
  }
  getShopSubCategoryLocalizedName(name: string, language: LocalizationLanguage): string | undefined {
    return this.localizationIndex['shopsubcategory_' + name.toLowerCase()]?.get(language) ?? undefined;
  }
  getDestinyBoardLocalizedName(id: string, language: LocalizationLanguage): string | undefined {
    return this.localizationIndex['destinyboard_title_' + id.toLowerCase()]?.get(language) ?? undefined;
  }
  private indexLocalizedNameById(localizedData: LocalizationData[]): void {
    for (let localized of localizedData) {
      this.localizationIndex[localized.id.toLowerCase()] = localized.localizedNames.languageMap;
    }
  }
  //#endregion

  //#region CRAFTING HELPER METHODS

  getItemResourceValue(item: Item): number | undefined {
    let resource = this._resourcesData.value.find((x) => x.name === item.resourceType)?.resourceTier.find((x) => x.tier === item.tier);
    if (!resource) return undefined;
    return resource.resourceValue;
  }
  getItemCraftFame(item: Item, craftingrequirements: CraftingRequirements): number | undefined {
    if (!item.craftingRequirements.length) return undefined;

    //if the item has a fameValue, it overrides the recursive math
    if (item.fameValue) return item.fameValue;

    return this.getCraftFameIterative(craftingrequirements);
  }
  //method to iterate and get the fames
  private getCraftFameIterative(requirements: CraftingRequirements): number {
    let result = 0;

    for (let ingredient of requirements.craftResources) {
      //get the item of the resource
      let ingredientName = ingredient.enchantmentLevel ? ingredient.uniqueName + `@${ingredient.enchantmentLevel}` : ingredient.uniqueName;
      let ingredientItem = this.getItemFromIndex(ingredientName);

      if (!ingredientItem) {
        if (this.shouldLog) console.log(`Could not find ingredient item for ${ingredientName}`);
        continue;
      }

      if (ingredientItem.fameValue) {
        result += ingredientItem.fameValue * ingredient.count;
        //if (this.shouldLog) console.log(`Fame increased by ${ingredientItem.fameValue} for each ${ingredient.count} ${ingredientItem.uniqueName}`);
      } else {
        //if (this.shouldLog) console.log(`Recursive fame math in ${ingredientItem.uniqueName}`);
        if (!ingredientItem.craftingRequirements.length) continue;
        result += this.getCraftFameIterative(ingredientItem.craftingRequirements[0]) * ingredient.count;
      }
    }
    return result;
  }
  //this method is unused. is this right?
  async getItemResourceFameValue(item: Item): Promise<number | undefined> {
    let resource = this._resourcesData.value.find((x) => x.name === item.resourceType)?.resourceTier.find((x) => x.tier === item.tier);
    if (!resource) return undefined;
    return resource.resourceFameValue;
  }
  getFocusToCraft(craftingRequirement: CraftingRequirements, focusEfficiency: number): number {
    return (craftingRequirement.craftingFocus ?? 0) * Math.pow(0.5, focusEfficiency / 10000);
  }
  getItemValue(item: Item): number {
    return this.getItemValueRecursive(item);
  }
  private getItemValueRecursive(item: Item): number {
    if (item.itemValue) return item.itemValue;
    if (!item.craftingRequirements.length) return 0;

    let value = 0;

    for (let ingredient of item.craftingRequirements[0].craftResources) {
      //get the item of the resource
      let ingredientName = ingredient.enchantmentLevel ? ingredient.uniqueName + `@${ingredient.enchantmentLevel}` : ingredient.uniqueName;
      let ingredientItem = this.getItemFromIndex(ingredientName);

      if (!ingredientItem) {
        if (this.shouldLog) console.log(`Could not find ingredient item for ${ingredientName}`);
        continue;
      }

      if (ingredientItem.itemValue !== undefined) {
        value += (ingredientItem.itemValue * ingredient.count) / (item.craftingRequirements[0].amountCrafted ?? 1);
        //if (this.shouldLog) console.log(`{${value}}[USED ITEM VALUE] Value increased by ${ingredientItem.itemValue} for each ${ingredient.count} ${ingredientItem.uniqueName}`);
      } else {
        //if (this.shouldLog) console.log(`{${value}}I had to go recursive getting item value for ${ingredientItem.usName} (${ingredientItem.uniqueName})`);
        value += (this.getItemValueRecursive(ingredientItem) * ingredient.count) / (item.craftingRequirements[0].amountCrafted ?? 1);
      }
    }
    //if (this.shouldLog) console.log(`===> ${item.usName} (${item.uniqueName}) returned ${value} item value`);
    return value;
  }
  getCraftingFee(item: Item, craftingUsageFee100Nutrition: number): number {
    if (item.tier && (item.tier === 2 || item.tier === 1)) {
      return 0;
    }
    return Math.round((this.getItemValue(item) * 0.1125 * craftingUsageFee100Nutrition) / 100);
  }
  // getTotalIngredientAmountAfterRRR(recipeRuns: number, amountPerRecepy: number, maxReturnAmount: number | undefined, rrr: number): { amountUsed: number; leftOverAmount: number } {
  //   let i = 1;
  //   let amountUsed = 0;
  //   let leftOverAmount = 0;
  //   while (i <= recipeRuns) {
  //     let amountUsedThisRun = amountPerRecepy - Math.floor(leftOverAmount);
  //     leftOverAmount -= Math.floor(leftOverAmount);
  //     let amountReturnedThisRun = Math.min((maxReturnAmount ?? Number.MAX_SAFE_INTEGER), amountPerRecepy * (rrr));
  //     amountUsed += amountUsedThisRun;
  //     leftOverAmount += amountReturnedThisRun;
  //     i++;
  //   }
  //   return { amountUsed: Math.ceil(amountUsed), leftOverAmount: Math.floor(leftOverAmount) };
  // }

  getTotalIngredientAmountAfterRRR(
    recipeRuns: number,
    amountPerRecipe: number,
    maxReturnAmount: number | undefined,
    rrr: number
  ): { amountUsed: number; leftOverAmount: number } {
    if (recipeRuns > 200) {
      const maxReturn = maxReturnAmount ?? Number.MAX_SAFE_INTEGER;
      const returnPerRecipe = Math.min(maxReturn, amountPerRecipe * rrr);

      const totalAmountUsed = amountPerRecipe * recipeRuns - Math.floor((recipeRuns - 1) * returnPerRecipe);
      const leftOverAmount = Math.floor(recipeRuns * returnPerRecipe - Math.floor((recipeRuns - 1) * returnPerRecipe));

      return { amountUsed: Math.ceil(totalAmountUsed), leftOverAmount: Math.floor(leftOverAmount) };
    } else {
      let amountUsed = 0;
      let leftOverAmount = 0;

      for (let i = 1; i <= recipeRuns; i++) {
        const amountUsedThisRun = amountPerRecipe - Math.floor(leftOverAmount);
        leftOverAmount -= Math.floor(leftOverAmount);

        const amountReturnedThisRun = Math.min(maxReturnAmount ?? Number.MAX_SAFE_INTEGER, amountPerRecipe * rrr);

        amountUsed += amountUsedThisRun;
        leftOverAmount += amountReturnedThisRun;
      }

      return {
        amountUsed: Math.ceil(amountUsed),
        leftOverAmount: Math.floor(leftOverAmount),
      };
    }
  }
  //#endregion

  //#region JSON LOADERS
  private loadItemsJsonData(): Observable<any> {
    if (isPlatformServer(this.platformId)) {
      return this.loadJson(environment.dataApiUrls.items.server);
    }
    return this.loadJson(environment.dataApiUrls.items.client);
  }
  private loadLocalizationJsonData(): Observable<any> {
    if (isPlatformServer(this.platformId)) {
      return this.loadJson(environment.dataApiUrls.localization.server);
    }
    return this.loadJson(environment.dataApiUrls.localization.client);
  }
  private loadResourcesJsonData(): Observable<any> {
    if (isPlatformServer(this.platformId)) {
      return this.loadJson(environment.dataApiUrls.resources.server);
    }
    return this.loadJson(environment.dataApiUrls.resources.client);
  }
  private loadAchievementsJsonData(): Observable<any> {
    if (isPlatformServer(this.platformId)) {
      return this.loadJson(environment.dataApiUrls.achievements.server);
    }
    return this.loadJson(environment.dataApiUrls.achievements.client);
  }
  private loadFarmingModifiersJsonData(): Observable<any> {
    if (isPlatformServer(this.platformId)) {
      return this.loadJson(environment.dataApiUrls.farmingModifiers.server);
    }
    return this.loadJson(environment.dataApiUrls.farmingModifiers.client);
  }
  private loadJson(url: string): Observable<any> {
    return this.http.get(url);
  }
  //#endregion

  //#region FARMING MODIFIERS

  private async loadFarmingModifiersData(_farmingModifiersJsonData: any): Promise<FarmingModifier[]> {
    return _farmingModifiersJsonData['farmingmodifiers']['location'].map((data: any) => {
      return {
        clusterId: data['@clusterid'],
        location: data['@clusterid'] ? getLocation(Number(data['@clusterid'])) : undefined,
        continent: toContinent(data['@continent']),
        biome: toBiome(data['@biome']),
        clusterQuality: toClusterQuality(data['@clusterquality']),
        farmingYieldModifier: this.mapFarmingYieldModifier(data['farmingyieldmodifier']),
      };
    });
  }

  private mapFarmingYieldModifier(data: any): FarmingYieldModifier[] {
    return data.map((x: any) => {
      return {
        uniqueName: x['@farmable'],
        value: Number(x['@value']),
        islandValue: Number(x['@islandvalue']),
        item: this.getItemFromIndex(x['@farmable']),
      };
    });
  }

  //#endregion

  //#region ITEMS
  private async loadItemsData(_itemsJsonData: any): Promise<Item[]> {
    if (this.shouldLog) console.time('a');
    const excludeItems = environment.excludeItems;
    const itemsData = [];
    for (let itemType in _itemsJsonData) {
      if (!excludeItems.includes(itemType)) {
        itemsData.push(...this.mapItems(_itemsJsonData[itemType]));
      }
    }
    if (this.shouldLog) console.timeEnd('a');

    //adds enchantments as items
    if (this.shouldLog) console.time('b');
    let enchantedItems = this.createItemsEnchantments(itemsData);
    itemsData.push(...enchantedItems);
    if (this.shouldLog) console.timeEnd('b');

    if (this.shouldLog) console.time('c');
    //adds journals full and empty as items
    let journalItems = this.createItemsJournalsFullEmpty(itemsData);
    itemsData.push(...journalItems);
    if (this.shouldLog) console.timeEnd('c');

    if (this.shouldLog) console.time('d');
    //add @ to items uniquename
    for (let item of itemsData) {
      if (item.enchantmentLevel && item.enchantmentLevel > 0) item.uniqueName = this.getUniqueNameWithAt(item.uniqueName, item.enchantmentLevel);
    }
    if (this.shouldLog) console.timeEnd('d');

    //sorts the items based on unique name
    if (this.shouldLog) console.time('e');
    itemsData.sort((a, b) => a.originalUniqueName.localeCompare(b.originalUniqueName));
    if (this.shouldLog) console.timeEnd('e');

    //if (this.shouldLog) console.log(this._itemsData.filter(x => !x.maxQualityLevel));

    return itemsData;
  }

  private addIngredientsAndUpgradeResItems(items: Item[]): void {
    for (let item of items) {
      for (let recipe of item.craftingRequirements) {
        for (let ing of recipe.craftResources) {
          ing.ingredientItem = this.getItemFromIndex(this.getUniqueNameWithAt(ing.uniqueName, ing.enchantmentLevel ?? 0)) ?? undefined;
        }
      }
      if (item.upgradeRequirements) {
        for (let upgrade of item.upgradeRequirements) {
          upgrade.item = this.getItemFromIndex(upgrade.uniqueName) ?? undefined;
        }
      }
    }
  }

  //this adds items objects to the validItems array of journals for ease of use
  private addItemsToJournals(items: Item[]): void {
    // Create a mapping (index) of items by their unique names.
    const itemsIndex: { [uniqueName: string]: Item[] } = {};
    for (let item of items) {
      const prefix = item.originalUniqueName;
      if (!itemsIndex[prefix]) {
        itemsIndex[prefix] = [];
      }
      itemsIndex[prefix].push(item);
    }

    let journalItems = items.filter((x) => x.journalCraftItemFame);
    for (let journal of journalItems) {
      // Add the valid items to the journal
      if (journal.journalCraftItemFame?.validItemsUniqueNames) {
        for (let uniqueName of journal.journalCraftItemFame.validItemsUniqueNames) {
          if (itemsIndex[uniqueName]) {
            journal.journalCraftItemFame.validItems.push(...itemsIndex[uniqueName]);
          }
        }
      }

      // Add the items to the journal loots
      if (journal.journalLoots) {
        for (let loot of journal.journalLoots) {
          if (loot.uniqueName) {
            loot.item = this.getItemFromIndex(loot.uniqueName) ?? undefined;
          }
        }
      }
    }
  }

  private addItemsToFarmStuff(items: Item[]): void {
    for (let item of items) {
      if (item.farmHarvest?.lootList?.lootItems && item.farmHarvest.lootList.lootItems.length > 0) {
        for (let loot of item.farmHarvest.lootList.lootItems) {
          loot.item = this.getItemFromIndex(loot.type) ?? undefined;
        }
      }

      if (item.farmProducts?.product?.lootList?.lootItems && item.farmProducts.product.lootList.lootItems.length > 0) {
        for (let loot of item.farmProducts.product.lootList.lootItems) {
          loot.item = this.getItemFromIndex(loot.type) ?? undefined;
        }
      }

      if (item.farmConsumption?.food?.acceptedFood?.favorite) {
        item.farmConsumption.food.acceptedFood.favoriteItem = this.getItemFromIndex(item.farmConsumption.food.acceptedFood.favorite);
      }

      if (item.farmGrownItem) {
        item.farmGrownItem.item = this.getItemFromIndex(item.farmGrownItem.uniqueName);
      }
    }
  }

  private getListOfUpgradeResources(items: Item[]): void {
    const upgradeResources: Item[] = [];
    items.forEach((item) => {
      if (item.upgradeRequirements && item.upgradeRequirements.length > 0) {
        item.upgradeRequirements.forEach((upgradeRequirement) => {
          if (upgradeRequirement.item) {
            upgradeResources.push(upgradeRequirement.item);
          }
        });
      }
    });
    this._upgradeResources.next([...new Set(upgradeResources)]);
  }

  public getUniqueNameWithAt(uniqueName: string, enchantmentLevel: number): string {
    if (enchantmentLevel > 0) return (uniqueName += `@${enchantmentLevel}`);
    else return uniqueName;
  }

  private createItemsEnchantments(items: Item[]): Item[] {
    const newItems: Item[] = [];
    for (const item of items) {
      // If the item has enchantments, create a clone for each enchantment
      if (item.enchantments && item.enchantments.length > 0) {
        for (const enchantment of item.enchantments) {
          const clonedItem: Item = item.clone();

          // Modify the clone item enchantment level
          clonedItem.enchantmentLevel = enchantment.enchantmentLevel;

          // Modify the crafting requirements from the enchantment
          clonedItem.craftingRequirements = enchantment.craftingRequirements;

          // Store the upgrade requirements from the enchantment
          clonedItem.upgradeRequirements = enchantment.upgradeRequirements.flatMap((x) => x.upgradeResources);

          // Modify the itemPower for the enchantment
          clonedItem.itemPower = enchantment.itemPower;

          // Add the cloned item to the result array
          newItems.push(clonedItem);
        }
      }
      if (!item.enchantmentLevel) item.enchantmentLevel = 0;
    }
    return newItems;
  }
  private createItemsJournalsFullEmpty(items: Item[]) {
    const newItems: Item[] = [];
    for (const journal of items.filter((x) => x.journalCraftItemFame)) {
      const full: Item = journal.clone();
      const empty: Item = journal.clone();

      full.uniqueName += '_FULL';
      full.originalUniqueName = full.uniqueName;
      empty.uniqueName += '_EMPTY';
      empty.originalUniqueName = empty.uniqueName;

      // we'll keep lootlist only on the full item
      empty.journalLoots = undefined;
      journal.journalLoots = undefined;

      newItems.push(full, empty);
    }
    return newItems;
  }
  private mapItems(itemsJsonData: any): Item[] {
    itemsJsonData = Array.isArray(itemsJsonData) ? itemsJsonData : [itemsJsonData];
    return itemsJsonData.map((itemJsonData: any) => {
      let item = new Item(this, this.userSettingsService);
      item.uniqueName = itemJsonData['@uniquename'];
      item.originalUniqueName = itemJsonData['@uniquename'];
      item.groupUniqueName = this.getGroupUniqueName(item.originalUniqueName);
      item.shopCategory = itemJsonData['@shopcategory'];
      item.shopSubCategory = itemJsonData['@shopsubcategory1'];
      item.craftingCategory = itemJsonData['@craftingcategory'];
      item.unlockedToCraft = itemJsonData['@unlockedtocraft'] === 'true';
      item.resourceType = itemJsonData['@resourcetype'];
      item.baseLootAmount = Number(itemJsonData['@baselootamount']) || undefined;
      item.fameValue = Number(itemJsonData['@famevalue']) || undefined;
      item.itemValue = Number(itemJsonData['@itemvalue']) || undefined;
      item.tier = Number(itemJsonData['@tier']) || undefined;
      item.weight = Number(itemJsonData['@weight']) || undefined;
      item.durability = Number(itemJsonData['@durability']) || undefined;
      item.enchantmentLevel = Number(itemJsonData['@enchantmentlevel']) || undefined;
      item.craftingRequirements = this.mapCraftingRequirements(itemJsonData['craftingrequirements']);
      item.craftFameFactor = Number(itemJsonData['@destinycraftfamefactor']) || undefined;
      item.journalCraftItemFame = this.mapJournalCraftItemFame(
        itemJsonData['famefillingmissions']?.['craftitemfame'] ??
          itemJsonData['famefillingmissions']?.['gatherfame'] ??
          itemJsonData['famefillingmissions']?.['fishingfame'] ??
          itemJsonData['famefillingmissions']?.['fameearned'] ??
          itemJsonData['famefillingmissions']?.['killmobfame']
      );
      item.journalLoots = this.mapJournalLoots(itemJsonData['lootlist']?.['loot']);
      item.journalMaxFame = Number(itemJsonData['@maxfame']) || undefined;
      item.enchantments = this.mapEnchantments(itemJsonData['enchantments']?.['enchantment']);
      item.itemPower = Number(itemJsonData['@itempower']) || undefined;
      item.combatSpechAchievement = itemJsonData['@combatspecachievement'];
      item.maxQualityLevel = Number(itemJsonData['@maxqualitylevel']) || undefined;
      item.slotType = getSlotTypeByName(itemJsonData['@slottype']);

      // FARM PROPS
      item.unlockedToPlace = itemJsonData['@unlockedtoplace'] === 'true';
      item.foodCategory = itemJsonData['@foodcategory'];
      item.nutrition = Number(itemJsonData['@nutrition']);
      item.farmPlaceFame = Number(itemJsonData['@placefame']) || undefined;
      item.farmKind = itemJsonData['@kind'];
      item.farmActiveFarmFocusCost = Number(itemJsonData['@activefarmfocuscost']) || undefined;
      item.farmActiveFarmMaxCycles = Number(itemJsonData['@activefarmmaxcycles']) || undefined;
      item.farmActiveFarmActionDurationSeconds = Number(itemJsonData['@activefarmactiondurationseconds']) || undefined;
      item.farmActiveFarmCycleLengthSeconds = Number(itemJsonData['@activefarmcyclelengthseconds']) || undefined;
      item.farmActiveFarmBonus = Number(itemJsonData['@activefarmbonus']) || undefined;
      item.farmHarvest = this.mapHarvest(itemJsonData['harvest']);
      item.farmGrownItem = this.mapFarmGrownItem(itemJsonData['grownitem']);
      item.farmConsumption = this.mapFarmConsumption(itemJsonData['consumption']);
      item.farmProducts = this.mapFarmProducts(itemJsonData['products']);
      return item;
    });
  }

  private mapFarmGrownItem(data: any): FarmGrownItem | undefined {
    if (!data) return undefined;

    return {
      uniqueName: data['@uniquename'],
      growTime: Number(data['@growtime']),
      fame: Number(data['@fame']),
      offspring: data.offspring
        ? {
            chance: Number(data.offspring['@chance']),
            amount: Number(data.offspring['@amount']),
          }
        : { chance: 0, amount: 0 },
    };
  }

  private mapFarmConsumption(data: any): FarmConsumption | undefined {
    if (!data?.food) return undefined;

    return {
      food: {
        nutritionMax: Number(data.food['@nutritionmax']),
        secondsPerNutrition: Number(data.food['@secondspernutrition']),
        lossBeforeHungry: Number(data.food['@lossbeforehungry']),
        acceptedFood: {
          foodCategory: data.food.acceptedfood['@foodcategory'],
          favorite: data.food.acceptedfood['@favorite'],
          favoriteBonus: Number(data.food.acceptedfood['@favoritebonus']),
        },
      },
    };
  }

  private mapFarmProducts(data: any): FarmProducts | undefined {
    if (!data?.product) return undefined;

    const product: FarmProduct = {
      productionTime: Number(data.product['@productiontime']),
      lootList: {
        name: data.product['@lootlist']['@name'],
        lootItems: this.mapLootItems(data.product['@lootlist']['Item']),
      },
      lootChance: Number(data.product['@lootchance']),
      fame: Number(data.product['@fame']),
    };

    return {
      product: product,
    };
  }

  private mapHarvest(harvestJsonData: any): Harvest | undefined {
    if (!harvestJsonData) {
      return undefined;
    }

    return {
      growTime: Number(harvestJsonData['@growtime']) || 0,
      lootList: {
        name: harvestJsonData['@lootlist']['@name'],
        lootItems: this.mapLootItems(harvestJsonData['@lootlist']['Item']),
      },
      lootChance: Number(harvestJsonData['@lootchance']) || 0,
      fame: Number(harvestJsonData['@fame']) || 0,
      seed: {
        chance: Number(harvestJsonData.seed['@chance']) || 0,
        amount: Number(harvestJsonData.seed['@amount']) || 0,
      },
    };
  }

  private mapLootItems(items: any[]): LootItem[] {
    if (!items) {
      return [];
    }

    if (!Array.isArray(items)) {
      items = [items];
    }

    return items.map((item) => {
      // Handle "3-6" format or single number
      const [min, max] = (item['@amount'] || '').split('-').map(Number);

      return {
        type: item['@type'],
        chance: Number(item['@chance']) || 0,
        amountMin: min || Number(item['@amount']) || 0,
        amountMax: max || min || Number(item['@amount']) || 0,
      };
    });
  }

  private getGroupUniqueName(originalUniqueName: string): string {
    // Regular expression to match 'T?_' pattern where '?' is a digit
    const startPattern = /^T\d_/;
    // Regular expression to match '_LEVEL?' pattern where '?' is a digit
    const endPattern = /_LEVEL\d$/;

    let modifiedName = originalUniqueName;

    // Check if the given name starts with 'T?_'
    if (startPattern.test(modifiedName)) {
      // Remove 'T?_' part from the start of the name
      modifiedName = modifiedName.replace(startPattern, '');
    }

    // Check if the given name ends with '_LEVEL?'
    if (endPattern.test(modifiedName)) {
      // Remove '_LEVEL?' part from the end of the name
      modifiedName = modifiedName.replace(endPattern, '');
    }

    return modifiedName;
  }

  private mapCraftingRequirements(craftingRequirementsJsonData: any): CraftingRequirements[] {
    if (!craftingRequirementsJsonData) {
      return [];
    }
    craftingRequirementsJsonData = Array.isArray(craftingRequirementsJsonData) ? craftingRequirementsJsonData : [craftingRequirementsJsonData];
    return craftingRequirementsJsonData.map((craftingRequirementJsonData: any) => ({
      silver: isNaN(parseFloat(craftingRequirementJsonData['@silver'])) ? undefined : parseFloat(craftingRequirementJsonData['@silver']),
      gold: isNaN(parseFloat(craftingRequirementJsonData['@gold'])) ? undefined : parseFloat(craftingRequirementJsonData['@gold']),
      time: isNaN(parseFloat(craftingRequirementJsonData['@time'])) ? undefined : parseFloat(craftingRequirementJsonData['@time']),
      amountCrafted: isNaN(parseFloat(craftingRequirementJsonData['@amountcrafted'])) ? undefined : parseFloat(craftingRequirementJsonData['@amountcrafted']),
      craftingFocus: isNaN(parseFloat(craftingRequirementJsonData['@craftingfocus'])) ? undefined : parseFloat(craftingRequirementJsonData['@craftingfocus']),
      craftResources: this.mapCraftResources(craftingRequirementJsonData['craftresource']),
      swapTransaction: craftingRequirementJsonData['@swaptransaction'] === 'true',
    }));
  }
  private mapCraftResources(craftResourcesJsonData: any): CraftResource[] {
    if (!craftResourcesJsonData) {
      return [];
    }
    craftResourcesJsonData = Array.isArray(craftResourcesJsonData) ? craftResourcesJsonData : [craftResourcesJsonData];
    return craftResourcesJsonData.map((craftResourceJsonData: any) => ({
      uniqueName: craftResourceJsonData['@uniquename'],
      enchantmentLevel: isNaN(parseFloat(craftResourceJsonData['@enchantmentlevel'])) ? undefined : parseFloat(craftResourceJsonData['@enchantmentlevel']),
      count: parseFloat(craftResourceJsonData['@count']),
      maxReturnAmount: isNaN(parseFloat(craftResourceJsonData['@maxreturnamount'])) ? undefined : parseFloat(craftResourceJsonData['@maxreturnamount']),
    }));
  }
  private mapJournalCraftItemFame(journalCraftItemJsonData: any): JournalCraftItemFame | undefined {
    if (!journalCraftItemJsonData) {
      return undefined;
    }
    return {
      minTier: isNaN(parseFloat(journalCraftItemJsonData['@mintier'])) ? undefined : parseFloat(journalCraftItemJsonData['@mintier']),
      value: isNaN(parseFloat(journalCraftItemJsonData['@value'])) ? undefined : parseFloat(journalCraftItemJsonData['@value']),
      validItemsUniqueNames: journalCraftItemJsonData['validitem']?.map((x: any) => x['@id']) ?? [],
      validItems: [],
    };
  }
  private mapJournalLoots(journalLootItemJsonData: any): JournalLoot[] | undefined {
    if (!journalLootItemJsonData) {
      return undefined;
    }
    journalLootItemJsonData = Array.isArray(journalLootItemJsonData) ? journalLootItemJsonData : [journalLootItemJsonData];
    const result: JournalLoot[] = [];
    journalLootItemJsonData.forEach((journalLootItem: any) => {
      result.push({
        item: undefined,
        uniqueName: !journalLootItem['@itemname']
          ? undefined
          : journalLootItem['@itemenchantmentlevel']
            ? journalLootItem['@itemname'] + '@' + journalLootItem['@itemenchantmentlevel']
            : journalLootItem['@itemname'],
        amount: isNaN(parseFloat(journalLootItem['@itemamount'])) ? 0 : parseFloat(journalLootItem['@itemamount']),
        silverAmount: isNaN(parseFloat(journalLootItem['@silveramount'])) ? 0 : parseFloat(journalLootItem['@silveramount']),
        weight: isNaN(parseFloat(journalLootItem['@weight'])) ? 0 : parseFloat(journalLootItem['@weight']),
        probability: 0,
      });
    });

    const totalWeight = result.reduce((acc, x) => acc + x.weight, 0);
    result.forEach((journalLoot) => {
      journalLoot.probability = journalLoot.weight / totalWeight;
    });
    return result;
  }
  private mapEnchantments(EnchantmentsJsonData: any): Enchantment[] {
    if (!EnchantmentsJsonData) {
      return [];
    }
    EnchantmentsJsonData = Array.isArray(EnchantmentsJsonData) ? EnchantmentsJsonData : [EnchantmentsJsonData];
    return EnchantmentsJsonData.map((EnchantmentJsonData: any) => ({
      enchantmentLevel: parseFloat(EnchantmentJsonData['@enchantmentlevel']),
      itemPower: isNaN(parseFloat(EnchantmentJsonData['@itempower'])) ? undefined : parseFloat(EnchantmentJsonData['@itempower']),
      craftingRequirements: this.mapCraftingRequirements(EnchantmentJsonData['craftingrequirements']),
      upgradeRequirements: this.mapUpgradeRequirements(EnchantmentJsonData['upgraderequirements']),
    }));
  }
  private mapUpgradeRequirements(upgradeRequirementsJsonData: any): CraftingRequirements[] {
    if (!upgradeRequirementsJsonData) {
      return [];
    }
    upgradeRequirementsJsonData = Array.isArray(upgradeRequirementsJsonData) ? upgradeRequirementsJsonData : [upgradeRequirementsJsonData];
    return upgradeRequirementsJsonData.map((upgradeRequirementJsonData: any) => ({
      upgradeResources: this.mapUpgradeResources(upgradeRequirementJsonData['upgraderesource']),
    }));
  }
  private mapUpgradeResources(upgradeResourcesJsonData: any): CraftResource[] {
    if (!upgradeResourcesJsonData) {
      return [];
    }
    upgradeResourcesJsonData = Array.isArray(upgradeResourcesJsonData) ? upgradeResourcesJsonData : [upgradeResourcesJsonData];
    return upgradeResourcesJsonData.map((upgradeResourceJsonData: any) => ({
      uniqueName: upgradeResourceJsonData['@uniquename'],
      count: parseFloat(upgradeResourceJsonData['@count']),
    }));
  }
  //#endregion

  //#region CATEGORIES
  private async loadShopCategoriesData(_itemsJsonData: any): Promise<ShopCategory[]> {
    let shopCategories = this.mapShopCategories(_itemsJsonData.shopcategories.shopcategory);

    this.setSubCategoriesCategories(shopCategories);

    //sort so it matches ingame order
    const sortedShopCategories = shopCategories.slice().sort((a, b) => a.displayName.localeCompare(b.displayName));

    sortedShopCategories.forEach((shopCategory) => {
      shopCategory.subCategories.sort((a, b) => a.displayName.localeCompare(b.displayName));
    });

    return sortedShopCategories;
  }

  private mapShopCategories(categoriesJsonData: any[]): ShopCategory[] {
    return categoriesJsonData.map((categoryJsonData) => {
      let shopCategory = new ShopCategory(this, this.userSettingsService);
      shopCategory.name = categoryJsonData['@id'];
      shopCategory.subCategories = this.mapShopSubCategories(categoryJsonData.shopsubcategory);
      return shopCategory;
    });
  }

  private mapShopSubCategories(subCategoriesJsonData: any[]): ShopSubCategory[] {
    return subCategoriesJsonData.map((subCategoryJsonData) => {
      let shopSubCategory = new ShopSubCategory(this, this.userSettingsService);
      shopSubCategory.name = subCategoryJsonData['@id'];
      return shopSubCategory;
    });
  }

  private setSubCategoriesCategories(shopCategories: ShopCategory[]): void {
    shopCategories.forEach((category) => {
      category.subCategories.forEach((subCategory) => {
        subCategory.shopCategory = category;
      });
    });
  }

  //#endregion

  //#region LOCALIZATION
  private async loadLocalizationData(_localizationJsonData: any): Promise<LocalizationData[]> {
    return this.mapLocalizationData(_localizationJsonData);
  }
  private mapLocalizationData(localizationJsonData: any[]): LocalizationData[] {
    return localizationJsonData.map((localizationData) => {
      const tuvArray = Array.isArray(localizationData.tuv) ? localizationData.tuv : [localizationData.tuv];
      let map = new Map<LocalizationLanguage, string>();

      for (let lang of getAllLocalizationLanguages()) {
        const tuvItem = tuvArray.find((tuvItem: any) => tuvItem && !!tuvItem['@xml:lang'] && tuvItem['@xml:lang'] === lang.id);
        // Check if the tuvItem is found before accessing 'seg'
        if (tuvItem) {
          map.set(lang, tuvItem['seg']);
        } else {
          // Handle the case where a language translation is missing
          // You can choose to set a default value or leave it out
          map.set(lang, 'Translation not available');
        }
      }

      return {
        id: localizationData['@tuid'],
        localizedNames: {
          languageMap: map,
        },
      };
    });
  }

  //#endregion

  //#region RESOURCES
  private async loadResourcesData(_resourcesJsonData: any): Promise<Resource[]> {
    let resources = _resourcesJsonData['AO-Resources']?.['Resources']?.['Resource'];
    return this.mapResourcesData(resources);
  }

  private mapResourcesData(resourcesJsonData: any[]): Resource[] {
    resourcesJsonData = Array.isArray(resourcesJsonData) ? resourcesJsonData : [resourcesJsonData];
    return resourcesJsonData.map((resourceJsonData) => ({
      name: resourceJsonData['@name'],
      resourceTier: this.mapResourceTier(resourceJsonData['ResourceTier']),
    }));
  }

  private mapResourceTier(resourceTiersJsonData: any[]): ResourceTier[] {
    resourceTiersJsonData = Array.isArray(resourceTiersJsonData) ? resourceTiersJsonData : [resourceTiersJsonData];
    return resourceTiersJsonData.map((resourceTierJsonData: any) => ({
      tier: parseFloat(resourceTierJsonData['@value']),
      resourceValue: parseFloat(resourceTierJsonData['@resourcevalue']),
      resourceFameValue: parseFloat(resourceTierJsonData['@famevalue']),
    }));
  }
  //#endregion

  //#region ACHIEVEMENTS
  private async loadAchievementsData(_achievementsJsonData: any): Promise<Achievement[]> {
    let achievements = _achievementsJsonData['achievements']?.['templateachievement'];
    return this.mapAchievementsData(achievements);
  }

  private mapAchievementsData(achievementsJsonData: any[]): Achievement[] {
    achievementsJsonData = Array.isArray(achievementsJsonData) ? achievementsJsonData : [achievementsJsonData];
    return achievementsJsonData.map((achievementJsonData) => {
      let achievement = new Achievement(this, this.userSettingsService);
      achievement.useTemplate = achievementJsonData['@usetemplate'];
      achievement.missionType = achievementJsonData['@missiontype'];
      achievement.id = achievementJsonData['@id'];
      achievement.category = achievementJsonData['@category'];
      achievement.itemForSprite = achievementJsonData['@itemforsprite'];
      achievement.isFameAchievement = JSON.parse(achievementJsonData['@isfameachievement']);
      achievement.achievementBonus = this.mapAchievementBonus(achievementJsonData['baserewards']?.['bonus']);
      return achievement;
    });
  }

  private mapAchievementBonus(achievementBonusJsonData: any[]): AchievementBonus[] | undefined {
    if (!achievementBonusJsonData) return undefined;
    achievementBonusJsonData = Array.isArray(achievementBonusJsonData) ? achievementBonusJsonData : [achievementBonusJsonData];

    return achievementBonusJsonData.map((_achievementBonusJsonData: any) => {
      const itempatternData = _achievementBonusJsonData['itempattern'];

      // Check if itempatternData is an array and map over it, otherwise handle the case (e.g., return an empty array or null)
      let itemPattern = Array.isArray(itempatternData) ? itempatternData : itempatternData ? [itempatternData] : undefined;

      return {
        type: _achievementBonusJsonData['@type'] ?? undefined,
        bonus: parseFloat(_achievementBonusJsonData['@bonus']),
        minTier: parseFloat(_achievementBonusJsonData['@mintier']),
        maxTier: parseFloat(_achievementBonusJsonData['@maxtier']),
        itemPattern: itemPattern?.map((x: any) => x['@pattern']),
      };
    });
  }
  //#endregion
}
